import {
  Role,
  useAddUserToOrganizationMutation,
  useGetAllOrganizationsQuery,
  useGetLogHistoryOfUserQuery,
  useGetUserQuery,
  useRemoveUserFromOrganizationMutation,
  useResetPasswordMutation,
  useUpdateUserRoleInOrganizationMutation,
} from "../generated/graphql";
import { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";

import {
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Table,
} from "../components/ui/table";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";
import { useToast } from "../components/ui/use-toast";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export function User() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const userId = id ?? "";
  const [page, setPage] = useState(1);
  const [role, setRole] = useState<Role>(Role.View);
  const [selectedOrg, setSelectedOrg] = useState("");

  const [{ data: userData, error: userError }, refetchUser] = useGetUserQuery({
    variables: { userId: userId },
    pause: !userId,
  });

  const [{ data: logsData, error: logsError }] = useGetLogHistoryOfUserQuery({
    variables: { userId, page: page },
    pause: !userId,
  });

  const [{ data: allOrganizationsData, error: allOrganizationsError }] =
    useGetAllOrganizationsQuery();

  const [, resetPassword] = useResetPasswordMutation();

  const [{ data: addUserToOrgData, error: addUserToOrgError }, addUserToOrg] =
    useAddUserToOrganizationMutation();

  const [{ data: updateRoleData, error: updateRoleError }, updateRole] =
    useUpdateUserRoleInOrganizationMutation();

  const [{ data: removeUserData, error: removeUserError }, removeUser] =
    useRemoveUserFromOrganizationMutation();

  useEffect(() => {
    if (!userId) {
      navigate("/");
    }
  }, [navigate, userId]);

  useEffect(() => {
    if (userError) {
      toast({
        title: "Error",
        description: userError.message,
        variant: "destructive",
      });
    }

    if (logsError) {
      toast({
        title: "Error",
        description: logsError.message,
        variant: "destructive",
      });
    }

    if (updateRoleError) {
      toast({
        title: "Error",
        description: updateRoleError.message,
        variant: "destructive",
      });
    }

    if (removeUserError) {
      toast({
        title: "Error",
        description: removeUserError.message,
        variant: "destructive",
      });
    }

    if (allOrganizationsError) {
      toast({
        title: "Error",
        description: allOrganizationsError.message,
        variant: "destructive",
      });
    }

    if (addUserToOrgError) {
      toast({
        title: "Error",
        description: addUserToOrgError.message,
        variant: "destructive",
      });
    }
  }, [
    addUserToOrgError,
    allOrganizationsError,
    logsError,
    removeUserError,
    toast,
    updateRoleError,
    userError,
  ]);

  useEffect(() => {
    if (updateRoleData) {
      toast({
        title: "Success",
        description: "Role updated successfully",
        variant: "default",
      });
      refetchUser({ requestPolicy: "network-only" });
    }

    if (removeUserData) {
      toast({
        title: "Success",
        description: "User removed successfully",
        variant: "default",
      });

      refetchUser({ requestPolicy: "network-only" });
    }

    if (addUserToOrgData) {
      toast({
        title: "Success",
        description: "User added to organization successfully",
        variant: "default",
      });
      refetchUser({ requestPolicy: "network-only" });
    }
  }, [addUserToOrgData, refetchUser, removeUserData, toast, updateRoleData]);

  const logs = logsData?.getLogHistoryOfUser;
  const user = userData?.getUser;
  const organizations = allOrganizationsData?.getAllOrganizations;

  return (
    <div className="flex min-h-screen w-full flex-col">
      <Navbar />
      <main className="flex flex-1 flex-col gap-4 p-4 md:gap-8 md:p-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">User</CardTitle>
            {/* <DollarSign className="h-4 w-4 text-muted-foreground" /> */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline">Edit</Button>
              </SheetTrigger>
              <SheetContent side="bottom">
                <SheetHeader>
                  <SheetTitle>Edit User</SheetTitle>
                </SheetHeader>
                <SheetFooter>
                  <SheetClose asChild>
                    <Button
                      onClick={async () => {
                        await resetPassword({ userId: id! });
                      }}
                    >
                      Reset Password
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold"></div>
            <p className="text-xs text-muted-foreground">
              Name: {user?.username}
            </p>
            <p className="text-xs text-muted-foreground">
              Email: {user?.email}
            </p>
          </CardContent>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="grid gap-2">
              <CardTitle>Organizations</CardTitle>
            </div>
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-black text-white"
                  onClick={() => {
                    setSelectedOrg(organizations?.at(0)?.id ?? "");
                  }}
                >
                  Add Organization
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom">
                <SheetHeader>
                  <SheetTitle>Add Organization</SheetTitle>
                </SheetHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="Organization" className="text-right">
                      Organization
                    </Label>
                    <Select
                      value={selectedOrg}
                      onValueChange={(v) => {
                        setSelectedOrg(v);
                      }}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Organization" />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations
                          ?.filter(
                            (organization) =>
                              !user?.organizations?.some(
                                (o) => o.id === organization.id
                              )
                          )
                          .map((organization, idx) => {
                            return (
                              <SelectItem key={idx} value={organization.id}>
                                {organization.name}
                              </SelectItem>
                            );
                          })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="Role" className="text-right">
                      Role
                    </Label>
                    <Select
                      value={role}
                      onValueChange={(v) => {
                        setRole(v as Role);
                      }}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={Role.Swap}>Swap</SelectItem>
                        <SelectItem value={Role.View}>View</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <SheetFooter>
                  <SheetClose asChild>
                    <Button
                      type="submit"
                      onClick={async () => {
                        if (!id) return;
                        await addUserToOrg({
                          userId: id,
                          organizationId: selectedOrg,
                          role: role,
                        });
                      }}
                    >
                      Save changes
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>id</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {user?.organizations?.map((organization, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{organization.id}</TableCell>
                    <TableCell>{organization.name}</TableCell>
                    <TableCell>{organization.role}</TableCell>
                    <TableCell className="text-right">
                      <Sheet>
                        <SheetTrigger asChild>
                          <Button
                            variant="outline"
                            className="bg-black text-white"
                            onClick={() => {
                              setRole(organization.role);
                            }}
                          >
                            Edit role
                          </Button>
                        </SheetTrigger>
                        <SheetContent side="bottom">
                          <SheetHeader>
                            <SheetTitle>
                              Edit Role for Org: {organization.id}
                            </SheetTitle>
                          </SheetHeader>
                          <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                              <Label htmlFor="Role" className="text-right">
                                Role
                              </Label>
                              <Select
                                value={role}
                                onValueChange={(v) => {
                                  setRole(v as Role);
                                }}
                              >
                                <SelectTrigger className="w-[180px]">
                                  <SelectValue placeholder="Role" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={Role.Swap}>
                                    Swap
                                  </SelectItem>
                                  <SelectItem value={Role.View}>
                                    View
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <SheetFooter>
                            <SheetClose asChild>
                              <Button
                                type="submit"
                                onClick={async () => {
                                  if (!id) return;
                                  await updateRole({
                                    userId: id,
                                    organizationId: organization.id,
                                    role: role,
                                  });
                                }}
                              >
                                Save changes
                              </Button>
                            </SheetClose>
                          </SheetFooter>
                        </SheetContent>
                      </Sheet>
                      <Button
                        variant="destructive"
                        onClick={async () => {
                          if (!id) return;
                          await removeUser({
                            userId: id,
                            organizationId: organization.id,
                          });
                        }}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader className="flex flex-row items-center">
            <div className="grid gap-2">
              <CardTitle>Logs</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>id</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs?.map((log, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{log.id}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>{log.date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => {
                      setPage(page - 1);
                    }}
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink>{page}</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={() => {
                      setPage(page + 1);
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
